'use client'

import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import cn from 'classnames'
import Link from 'next/link'
import {Icon} from 'src/common/Icon'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import type {UrlObject} from 'url'

export type InfoBannerBadge = {
  label: string
}

export type InfoBannerProps = {
  sliceType: string
  sliceVariation: string
  paddingTopClass: string
  paddingBottomClass: string
  title: string
  badges: InfoBannerBadge[]
  description: string
  buttonLabel: string
  buttonHref?: string | UrlObject
  buttonTarget?: string
  buttonMarketingId: string
  pageUid: string
}

export const InfoBanner = ({
  sliceType,
  sliceVariation,
  paddingTopClass,
  paddingBottomClass,
  title,
  badges,
  description,
  buttonLabel,
  buttonHref,
  buttonTarget,
  buttonMarketingId,
  pageUid,
}: InfoBannerProps) => {
  const domainCountry = useGetDomainCountry()
  const hasButton = Boolean(buttonLabel && buttonHref)

  const trackButtonClick = () => {
    trackMossAnalytics({
      event: 'interaction',
      interaction: {
        element: buttonMarketingId || 'info-banner',
        elementText: buttonLabel,
        location: 'info-banner',
        type: 'click',
      },
      page: {domainCountry, type: pageUid || 'unknown'},
    })
  }

  if (!title && badges.length === 0 && !description && !hasButton) {
    return null
  }

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={sliceVariation}
      data-nav-dark
      className={cn('bg-green-dark', paddingTopClass, paddingBottomClass)}
    >
      <div className="mx-auto max-w-[1280px] px-5 md:px-10">
        <div className="flex flex-col gap-6 rounded-lg bg-green-light px-8 pb-10 pt-8 lg:flex-row">
          <div className="flex flex-1 flex-col gap-6">
            {title && <h2 className="text-40 leading-tight tracking-[-0.21px] text-moss-white">{title}</h2>}
            {badges.length > 0 && (
              <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:gap-4">
                {badges.map((badge, badgeIndex) => (
                  <div className="flex items-center gap-4" key={badgeIndex}>
                    <div className="flex flex-row-reverse items-center gap-3 lg:flex-row">
                      <span className="text-13 font-semibold uppercase tracking-[0.78px] text-moss-white">
                        {badge.label}
                      </span>
                      <span
                        aria-hidden="true"
                        className="flex size-5 items-center justify-center rounded-full bg-green-neon"
                      >
                        <Icon name="check-bold" className="size-3 text-green-dark" />
                      </span>
                    </div>
                    {badgeIndex < badges.length - 1 && (
                      <div className="hidden h-[17px] w-px bg-moss-white/20 lg:block" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-1 flex-col gap-6">
            {description && <p className="text-18 text-moss-white">{description}</p>}
            {hasButton && (
              <div>
                <Link
                  href={buttonHref as string | UrlObject}
                  className="text-button-s inline-flex h-12 items-center rounded-[4px] border border-moss-white bg-transparent px-4 py-3 text-moss-white transition-colors hover:bg-moss-white hover:text-moss-black"
                  onClick={trackButtonClick}
                  {...(buttonTarget && {target: buttonTarget, rel: 'noopener noreferrer'})}
                >
                  {buttonLabel}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
